import imageCompression from 'browser-image-compression';

import axiosInstance, { endpoints } from 'src/lib/axios-artham';

// ----------------------------------------------------------------------

export interface UploadResult {
  url: string;
  key: string;
  size: number;
  contentType: string;
}

export interface UploadError {
  error: string;
  code: string;
}

interface CompressionProgress {
  progress: number; // 0-100
  stage: 'compressing' | 'uploading';
}

// ----------------------------------------------------------------------

/**
 * Compress image file before upload
 * Target: Reduce to ~2MB while maintaining quality
 */
export async function compressImage(
  file: File,
  onProgress?: (progress: number) => void
): Promise<File> {
  // If file is already small enough (< 2MB), return as is
  const maxSizeMB = 2;
  if (file.size <= maxSizeMB * 1024 * 1024) {
    onProgress?.(100);
    return file;
  }

  try {
    const options = {
      maxSizeMB,
      maxWidthOrHeight: 1920, // Max dimension
      useWebWorker: true,
      fileType: file.type,
      onProgress: (progress: number) => {
        // Progress is 0-100
        onProgress?.(progress);
      },
    };

    const compressedFile = await imageCompression(file, options);

    console.log(
      `Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`
    );

    return compressedFile;
  } catch (error) {
    console.error('Image compression failed:', error);
    // Return original file if compression fails
    return file;
  }
}

/**
 * Upload image to server with automatic compression
 */
export async function uploadImage(
  file: File,
  options?: {
    onProgress?: (progress: CompressionProgress) => void;
    skipCompression?: boolean;
  }
): Promise<UploadResult | UploadError> {
  try {
    // Step 1: Compress image
    let fileToUpload = file;

    if (!options?.skipCompression) {
      options?.onProgress?.({ progress: 0, stage: 'compressing' });

      fileToUpload = await compressImage(file, (progress) => {
        options?.onProgress?.({
          progress: progress * 0.5, // Compression is 50% of total progress
          stage: 'compressing',
        });
      });

      options?.onProgress?.({ progress: 50, stage: 'compressing' });
    }

    // Step 2: Upload to server
    options?.onProgress?.({ progress: 50, stage: 'uploading' });

    const formData = new FormData();
    formData.append('file', fileToUpload);

    const response = await axiosInstance.post<{
      success: boolean;
      data: UploadResult;
      error?: string;
      code?: string;
    }>(endpoints.upload.image, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const uploadProgress = (progressEvent.loaded / progressEvent.total) * 100;
          const totalProgress = 50 + uploadProgress * 0.5; // Upload is remaining 50%
          options?.onProgress?.({
            progress: totalProgress,
            stage: 'uploading',
          });
        }
      },
    });

    if (response.data.success && response.data.data) {
      options?.onProgress?.({ progress: 100, stage: 'uploading' });
      return response.data.data;
    }

    // Handle error response
    return {
      error: response.data.error || 'Upload failed',
      code: response.data.code || 'UPLOAD_FAILED',
    };
  } catch (error: any) {
    console.error('Upload error:', error);

    // Handle axios error response
    if (error.response?.data) {
      return {
        error: error.response.data.error || 'Upload failed',
        code: error.response.data.code || 'UPLOAD_FAILED',
      };
    }

    // Handle network errors
    return {
      error: error.message || 'Network error occurred',
      code: 'NETWORK_ERROR',
    };
  }
}

/**
 * Upload image with automatic retry on failure
 */
export async function uploadImageWithRetry(
  file: File,
  options?: {
    onProgress?: (progress: CompressionProgress) => void;
    skipCompression?: boolean;
    maxRetries?: number;
  }
): Promise<UploadResult | UploadError> {
  const maxRetries = options?.maxRetries || 3;
  let lastError: UploadError = { error: 'Unknown error', code: 'UNKNOWN' };

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    console.log(`Upload attempt ${attempt}/${maxRetries}`);

    const result = await uploadImage(file, options);

    // Success
    if ('url' in result) {
      return result;
    }

    // Error - save it and retry (except for validation errors)
    lastError = result;

    // Don't retry validation errors
    if (['INVALID_TYPE', 'FILE_TOO_LARGE', 'INVALID_FILE'].includes(result.code)) {
      break;
    }

    // Wait before retry (exponential backoff: 1s, 2s, 4s)
    if (attempt < maxRetries) {
      const delay = Math.pow(2, attempt - 1) * 1000;
      console.log(`Retrying in ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  return lastError;
}

/**
 * Validate image file before upload
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  if (!file) {
    return { valid: false, error: 'No file selected' };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${allowedTypes.join(', ')}`,
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${maxSize / 1024 / 1024}MB`,
    };
  }

  return { valid: true };
}
